import { useRef, useState } from 'react'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material'
import Button from '@mui/material/Button'
import { useFormik } from 'formik'
import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'
import { read, utils } from 'xlsx'
import GridOnIcon from '@mui/icons-material/GridOn'

import { message } from 'antd'
import { axiosInstance } from '../../../hooks/useAxios'
import { ERRORS_MESSAGE_MAPPER } from '../../../constants/errors'
import CircularLoading from '../../../components/CircularProgress'
import { isEmpty } from 'radash'

interface Props {
  isOpen: boolean
  onClose: (visible: boolean) => void
  onUpdateProductSuccess: () => void
}

export const ProductImportFormModal = (props: Props) => {
  const { isOpen, onClose, onUpdateProductSuccess } = props
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const inputFile = useRef<HTMLInputElement>(null)

  const formik = useFormik<any>({
    initialValues: {
      data: '',
    },
    onSubmit: async (values: any) => {
      setIsLoading(true)
      if (isEmpty(values)) {
        message.error('กรุณาเลือกไฟล์ Excel ก่อน')
        setIsLoading(false)
        return
      }
      try {
        await axiosInstance.post(`/product/import`, values.data)
        onUpdateProductSuccess()
        message.success('นำเข้าสินค้าสำเร็จ')
        formik.resetForm()
        onClose(false)
      } catch (e: any) {
        const messageError =
          ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
          'บันทึกข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
      setIsLoading(false)
    },
  })

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (event) => {
        const wb = read(event.target!.result)
        const sheets = wb.SheetNames
        if (sheets.length) {
          const rows: any[] = utils.sheet_to_json(wb.Sheets[sheets[0]])
          const data = rows
            .map((row) => {
              return {
                name: row['Name'] || '',
                code: row['Code'] || '',
                description: row['Description'] || '',
                price: Number(row['Price'] || 0),
                cost: Number(row['Cost'] || 0),
                commission: Number(row['Commission'] || 0),
                category: row['Category'] || '',
                quantity: row['Amount'] || '',
                size: row['Size'] || '',
                weight: row['Weight'] || '',
                color: row['Color'] || '',
              }
            })
            .filter((row) => row.name)

          if (data.length > 100) {
            inputFile.current!.value = ''
            message.error('ไม่สามารถนำเข้าข้อมูลเกิน 100 รายการต่อครั้ง')
            return
          }
          const uniqueArray = data.filter(
            (obj, index, self) =>
              index ===
              self.findIndex((o) => o.name === obj.name && o.code === obj.code),
          )
          formik.setFieldValue('data', uniqueArray)
          //setOrderList(lowerCaseKeys)
        }
      }
      reader.readAsArrayBuffer(file)
    }
  }

  return (
    <Dialog
      open={isOpen}
      onClose={() => onClose(false)}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '800px!important',
        },
      }}
    >
      <CircularLoading isLoading={isLoading} />
      <DialogTitle>นำเข้าสินค้าจาก Excel</DialogTitle>
      <DialogContent>
        <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '50%',
                margin: '0 auto',
              }}
            >
              <input
                style={{
                  padding: '10px',
                }}
                ref={inputFile}
                type="file"
                name="file"
                className="custom-input-file"
                required
                onChange={handleImport}
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              />
            </Box>
            <Box
              mt={1}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <Typography variant="body1">หมายเหตุ</Typography>
              <Typography variant="body2" mb={1} color="text.secondary">
                แนะนำให้ใช้ไฟล์ Excel ที่มีรูปแบบตามตัวอย่างที่กำหนด
              </Typography>
              <Button
                variant="outlined"
                color="info"
                size="large"
                startIcon={<GridOnIcon fontSize="large" />}
                component={'a'}
                href={'/excels/import-products.xlsx'}
              >
                ดาวน์โหลดตัวอย่างไฟล์
              </Button>
            </Box>
          </Box>
          <Grid container spacing={2} mt={2} justifyContent={'center'}>
            <Grid item>
              <Button
                startIcon={<SaveIcon />}
                color="primary"
                type="submit"
                size="large"
                variant="contained"
              >
                บันทึก
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="inherit"
                size="large"
                variant="contained"
                onClick={() => onClose(false)}
                startIcon={<ClearIcon />}
              >
                ปิดหน้าต่าง
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
